<template>
  <div class="flex flex-col gap-6">
    <!-- Page Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-slate-800">项目数据管理</h1>
        <p class="text-sm text-slate-500 mt-1">智能检索科研项目文档与资料</p>
      </div>
      <div class="flex items-center gap-3">
        <span class="px-3 py-1.5 bg-purple-50 text-purple-600 rounded-full text-xs font-semibold">数据管理</span>
      </div>
    </div>

    <!-- Search Box -->
    <div class="glass-card rounded-2xl p-6">
      <div class="flex flex-col gap-4">
        <div class="flex items-center gap-3">
          <div class="flex-1 relative">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="输入关键词、项目名称、申报人..."
              class="premium-input w-full rounded-xl pl-12 pr-4 py-4 text-base"
              @keyup.enter="handleSearch"
            />
            <svg class="w-6 h-6 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
          <button class="px-4 py-4 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center gap-2 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
            </svg>
            <span class="text-sm font-medium">高级筛选</span>
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
          <button class="btn-action px-6 py-4 rounded-xl text-white font-medium flex items-center gap-2" @click="handleSearch">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            智能检索
          </button>
        </div>
      </div>
    </div>

    <!-- Category Tabs -->
    <div class="flex flex-wrap gap-2">
      <button
        v-for="category in categories"
        :key="category.key"
        class="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300"
        :class="activeCategory === category.key ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'"
        @click="activeCategory = category.key"
      >
        {{ category.label }}
      </button>
    </div>

    <!-- Search Results Info -->
    <div class="flex items-center justify-between">
      <span class="text-sm text-slate-500">
        共找到 <span class="font-semibold text-slate-700">{{ totalResults.toLocaleString() }}</span> 条结果，
        耗时 <span class="font-semibold text-slate-700">{{ searchTime }}</span> 秒
      </span>
    </div>

    <!-- Search Results -->
    <div class="flex flex-col gap-4">
      <div
        v-for="(result, index) in searchResults"
        :key="result.id"
        class="glass-card rounded-2xl p-6 hover:shadow-lg transition-shadow"
      >
        <!-- Result Header -->
        <div class="flex items-start justify-between mb-3">
          <div class="flex items-center gap-3">
            <span class="text-2xl">📄</span>
            <h3 class="text-lg font-semibold text-slate-800">{{ result.title }}</h3>
            <span class="text-sm text-slate-400">- {{ result.type }}</span>
          </div>
          <div class="flex items-center gap-2">
            <button class="px-4 py-2 rounded-lg text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors">
              查看详情
            </button>
            <button class="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">
              预览
            </button>
            <button class="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">
              下载
            </button>
          </div>
        </div>

        <!-- Tags -->
        <div class="flex flex-wrap items-center gap-2 mb-3">
          <span class="px-2.5 py-1 rounded-md bg-blue-50 text-blue-600 text-xs font-medium">{{ result.fundType }}</span>
          <span class="px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 text-xs font-medium">{{ result.year }}</span>
          <span class="px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 text-xs font-medium">{{ result.pages }}页</span>
          <span class="px-2.5 py-1 rounded-md bg-red-50 text-red-600 text-xs font-medium">{{ result.format }}</span>
          <span class="px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-600 text-xs font-medium">{{ result.access }}</span>
        </div>

        <!-- Meta Info -->
        <div class="flex items-center gap-6 text-sm text-slate-500 mb-3">
          <span>负责人: <span class="text-slate-700 font-medium">{{ result.leader }}</span></span>
          <span>单位: <span class="text-slate-700 font-medium">{{ result.unit }}</span></span>
          <span>上传: <span class="text-slate-700 font-medium">{{ result.uploadDate }}</span></span>
        </div>

        <!-- Abstract -->
        <div class="mb-3">
          <p class="text-sm text-slate-600 leading-relaxed">
            <span class="font-medium text-slate-700">摘要:</span> {{ result.abstract }}
          </p>
        </div>

        <!-- Keywords -->
        <div class="flex items-center gap-2 mb-3">
          <span class="text-sm text-slate-500">关键词:</span>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="keyword in result.keywords"
              :key="keyword"
              class="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-xs cursor-pointer hover:bg-blue-50 hover:text-blue-600 transition-colors"
              @click="searchQuery = keyword; handleSearch()"
            >
              {{ keyword }}
            </span>
          </div>
        </div>

        <!-- Similar Recommendation -->
        <div v-if="result.similarRecommendation" class="flex items-center gap-2 mb-3 p-3 rounded-xl bg-amber-50">
          <span class="text-amber-500">📈</span>
          <span class="text-sm text-amber-700">
            相似推荐: "{{ result.similarRecommendation }}"
          </span>
        </div>

        <!-- Duplicate Warning -->
        <div v-if="result.duplicateWarning" class="flex items-center gap-2 p-3 rounded-xl bg-red-50">
          <span class="text-red-500">🔴</span>
          <span class="text-sm text-red-700">
            相似度警告: {{ result.duplicateWarning }}
          </span>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="flex items-center justify-between pt-4">
      <div class="flex items-center gap-2">
        <button class="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50" :disabled="currentPage === 1">
          ⬅️ 上一页
        </button>
        <div class="flex items-center gap-1">
          <button
            v-for="page in displayedPages"
            :key="page"
            class="px-3 py-2 rounded-lg text-sm font-medium transition-colors"
            :class="page === currentPage ? 'bg-blue-500 text-white' : 'text-slate-600 hover:bg-slate-100'"
            @click="currentPage = page"
          >
            {{ page }}
          </button>
          <span v-if="showEllipsis" class="px-2 text-slate-400">...</span>
          <button
            v-for="page in endPages"
            :key="page"
            class="px-3 py-2 rounded-lg text-sm font-medium transition-colors"
            :class="page === currentPage ? 'bg-blue-500 text-white' : 'text-slate-600 hover:bg-slate-100'"
            @click="currentPage = page"
          >
            {{ page }}
          </button>
        </div>
        <button class="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
          下一页 ➡️
        </button>
      </div>
      <div class="flex items-center gap-4 text-sm text-slate-500">
        <span>每页 {{ pageSize }} 条</span>
        <span>共 {{ totalPages }} 页</span>
      </div>
    </div>

    <!-- Related Searches -->
    <div class="flex items-center gap-3 pt-4 border-t border-slate-200">
      <span class="text-sm text-slate-500">💡 试试相关搜索:</span>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="term in relatedSearches"
          :key="term"
          class="px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 text-sm hover:bg-blue-50 hover:text-blue-600 transition-colors"
          @click="searchQuery = term; handleSearch()"
        >
          "{{ term }}"
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KnowledgeBaseSearch',
  data() {
    return {
      searchQuery: '',
      activeCategory: 'all',
      currentPage: 1,
      pageSize: 20,
      totalResults: 2847,
      totalPages: 143,
      searchTime: '0.23',
      categories: [
        { key: 'all', label: '全部' },
        { key: 'guide', label: '计划指南' },
        { key: 'declaration', label: '申报书' },
        { key: 'task', label: '任务书' },
        { key: 'midterm', label: '中期报告' },
        { key: 'annual', label: '年度报告' },
        { key: 'final', label: '结题报告' },
        { key: 'other', label: '其他' }
      ],
      searchResults: [
        {
          id: 1,
          title: '高熵催化体系的设计...',
          type: '申报书',
          fundType: '自然科学基金',
          year: '2025',
          pages: 156,
          format: 'PDF',
          access: '公开',
          leader: '张明',
          unit: 'XX大学',
          uploadDate: '2025-03-28',
          abstract: '本项目拟开展高熵催化体系的设计研究，通过调控高熵合金的组成和结构，开发新型高效催化剂，用于能源转化和环境保护领域。项目将系统研究高熵效应与催化性能之间的构效关系，建立高熵催化剂的设计原则。',
          keywords: ['高熵催化', '锂硫电池', '低温驱动', '能源存储'],
          similarRecommendation: '多孔碳材料在锂硫电池中的应用',
          duplicateWarning: null
        },
        {
          id: 2,
          title: '高熵催化材料性能研究',
          type: '结题报告',
          fundType: '自然科学基金',
          year: '2024',
          pages: 89,
          format: 'PDF',
          access: '公开',
          leader: '李华',
          unit: 'XX研究院',
          uploadDate: '2024-12-15',
          abstract: '本项目完成了高熵催化材料的设计、制备及性能研究，建立了系统的材料表征方法，揭示了高熵效应对催化性能的影响机制。研究成果发表SCI论文12篇，申请发明专利5项。',
          keywords: ['高熵材料', '催化性能', '构效关系'],
          similarRecommendation: null,
          duplicateWarning: '与检索内容重复率 23%'
        },
        {
          id: 3,
          title: '新型储能材料的设计与开发',
          type: '任务书',
          fundType: '重点研发计划',
          year: '2025',
          pages: 45,
          format: 'PDF',
          access: '内部',
          leader: '王强',
          unit: 'XX科技大学',
          uploadDate: '2025-01-20',
          abstract: '本项目旨在开发新型储能材料，提升电池能量密度和循环寿命。研究内容包括材料设计、合成工艺优化、性能测试及应用验证等方面。',
          keywords: ['储能材料', '电池技术', '能量密度'],
          similarRecommendation: '锂离子电池正极材料研究进展',
          duplicateWarning: null
        }
      ],
      relatedSearches: ['电化学催化', '储能材料', '新能源电池']
    }
  },
  computed: {
    displayedPages() {
      const pages = []
      const start = Math.max(1, this.currentPage - 2)
      const end = Math.min(start + 2, this.totalPages)
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    },
    showEllipsis() {
      return this.totalPages > 5 && this.currentPage < this.totalPages - 2
    },
    endPages() {
      const pages = []
      if (this.totalPages > 5) {
        for (let i = Math.max(this.totalPages - 2, 4); i <= this.totalPages; i++) {
          if (!this.displayedPages.includes(i)) {
            pages.push(i)
          }
        }
      }
      return pages
    }
  },
  methods: {
    handleSearch() {
      // Simulate search action
      console.log('Searching for:', this.searchQuery)
      // In real implementation, this would trigger API call
    }
  }
}
</script>
